import { html, TemplateResult, CSSResultGroup } from 'lit';
import { property, query, queryAssignedElements, queryAssignedNodes, state } from 'lit/decorators.js';
import { nanoid } from 'nanoid';
import { BlocksTabChange } from '../../events/blocks-tab-change';
import { scrollIntoView } from '../../util/scroll';
import { BlocksElement } from '../BlocksElement';
import { BlocksTabPanel } from '../tab-panel/tab-panel';
import { BlocksTab } from '../tab/tab';
import type { TabsBehavior, TabSize, TabVariant } from './tabs.model';
import styles from './tabs.styles';

let tabId = 1;

/**
 * @slot default - This displays sequence of tab given
 * @slot panel - The tab panel content
 * @fires bl-tab-change - emitted when user changes from one tab to other
 * @fires bl-tab-select - emitted when a tab is selected
 * @csspart base - The component's base wrapper.
 * @csspart header - The component's header element.
 * @csspart list - The component's list element.
 * @csspart part-default - The component's default slot.
 * @csspart body - The component's body wrapper.
 * @csspart part-panel - The component's panel slot.
 */

export class BlocksTabs extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Active tab state
   * 1) Sets the initial active tab (e.g. 0 sets the first tab, 1 sets the second tab, etc.)
   */
  @property({ type: Number })
  activeIndex: number | undefined;

  /**
   * Behavior variant is marked as deprecated and will be removed in next major release. Application team can use css to achieve this behavior according to their need.
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**responsive-full-width** allows for natural width on large screens and full width on small screens</li>
   * <li>**full-width** allows  full width on all screens</li>
   * </ul>
   * </cat-text-passage>
   * @deprecated
   */
  @property({ type: String })
  behavior: TabsBehavior;

  /**
   * Style variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**span-container** Expands the border of the tab container to full width</li>
   * <li>**contrast** variant is marked as deprecated and will be removed in next major release. The replacement for this is `contained` variant. </li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  variant: TabVariant;

  /** size variants is marked as deprecated and will be removed in next major release. No alternate for this size variant.
   * @deprecated
   * **lg** adds extra padding to the default tab
   */
  @property({ type: String })
  size: TabSize;

  @queryAssignedNodes({ slot: 'panel', flatten: true }) private _panels: BlocksTabPanel[] | undefined;

  @queryAssignedElements({ flatten: true }) private tabLists: BlocksTab[] | undefined;
  /**
   * Active tab state
   */
  @state()
  private activeTab: BlocksTab | undefined;

  /**
   * If position from left is greater than 0, set isStart to false. Otherwise set isStart to true.
   */
  @state()
  private isStart = true;

  /**
   * If last child is fully in the viewport, set isEnd to true. Otherwise, set isEnd to false.
   */
  @state()
  private isEnd = false;

  /**
   * Tab header button's wrapper container element
   */
  @query('.cat-c-tabs__list')
  private tabsHeaderEl: HTMLElement | undefined;

  private get isLTR() {
    return document.dir === 'ltr' || document.dir === '';
  }

  /**
   * Auto Increment id to sync tab and tab panel
   */
  private tabId: string;

  private get panels(): Array<BlocksTabPanel> | undefined {
    if (this._panels) {
      return Array.from(this._panels);
    }
  }

  constructor() {
    super();
    this.tabId = 'cat-tabpanel-' + tabId++;
  }

  /**
   * On overflow list inner scroll
   * 1) On scroll, if position from left is greater than 0, set isStart to false. Otherwise set isStart to true.
   * 2) On scroll, If last child is fully in the viewport, set isEnd to true. Otherwise, set isEnd to false.
   */

  private onScroll = (): void => {
    this.setIsStart(); /* 1 */
    this.setIsEnd(); /* 2 */
  };

  /**
   * Set isStart state
   * 1) If position from left is greater than 0, set isStart to false. Otherwise set isStart to true.
   */
  private setIsStart = (): void => {
    if (this.isLTR) {
      if (this.tabsHeaderEl && this.tabsHeaderEl.scrollLeft > 0) {
        this.isStart = false; /* 1 */
      } else {
        this.isStart = true; /* 1 */
      }
    } else {
      if (this.tabsHeaderEl && this.tabsHeaderEl.scrollLeft < 0) {
        this.isStart = false; /* 1 */
      } else {
        this.isStart = true; /* 1 */
      }
    }
  };

  /**
   * Set isEnd state
   * 1) If scroll reach right end, set isEnd to true. Otherwise set isEnd to false.
   */
  private setIsEnd(): void {
    if (this.tabsHeaderEl) {
      const currentScrollPos = this.tabsHeaderEl.scrollLeft;
      const maxScrollWidth = this.tabsHeaderEl.scrollWidth - this.tabsHeaderEl.clientWidth;
      if (this.isLTR) {
        if (Math.ceil(currentScrollPos) >= maxScrollWidth) {
          this.isEnd = true;
        } else {
          this.isEnd = false;
        }
      } else {
        if (Math.floor(currentScrollPos) <= -maxScrollWidth) {
          this.isEnd = true;
        } else {
          this.isEnd = false;
        }
      }
    }
  }

  protected firstUpdated() {
    this.syncTabAndSetActive();
  }

  /**
   * 1) Auto set the `aria-labelledby` prop for accessible tabs if user doesn't define the `ariaLabelledBy` prop
   * 2) Initialize tabs Set the active tab if activeIndex is defined. Otherwise, set the first tab as active by default
   */
  private async syncTabAndSetActive() {
    this.syncTabWithPanel(); /**1 */
    await this.updateComplete;
    if (this.tabLists) {
      this.activeTab = this.activeIndex ? this.tabLists[this.activeIndex] : this.tabLists[0]; /**2 */
    }
    this.activateTab();
  }

  /**
   * 1) Auto set the `aria-labelledby`, `variant`, prop for accessible tabs and tabs panel to sync
   *
   */
  private syncTabWithPanel(): void {
    this.tabLists?.forEach((tabItem, index) => {
      const ariaLabelledBy = tabItem.ariaLabelledBy;
      const id = ariaLabelledBy || nanoid();
      tabItem.ariaLabelledBy = id; /* 1 */
      this.setTabItemProps(tabItem);
      tabItem.index = index;
      const controlId = this.tabId + `-${index}${ariaLabelledBy ? '-' + ariaLabelledBy : ''}`;
      tabItem.ariaControls = controlId;
      const panel = this.panels?.[index];
      panel?.setAttribute('id', controlId);
      panel?.setAttribute('aria-labelledby', id);
    });
  }

  /**
   * Pass variant, behavior and size props to TabItems children
   * @param tabItem - current TabItem to set all the props
   * 1) Set `size` property to tabItem only when variant is other than `contrast`
   */
  private setTabItemProps(tabItem: BlocksTab) {
    tabItem.variant = this.variant;
    tabItem.behavior = this.behavior;
    if (this.variant !== 'contrast') {
      tabItem.size = this.size; /* 1 */
    }
    this.setIsStart();
    this.setIsEnd();
  }

  private activateTab(): void {
    if (this.activeTab?.disabled) {
      return;
    }

    if (this.activeTab) {
      this.activeTab.isActive = true;
    }

    const id = `#${this.activeTab?.ariaControls}`;
    const panel = this.querySelector<BlocksTabPanel>(`${id}`);

    if (panel) {
      panel.selected = true;
    }
  }

  private focusTabElement(): void {
    this.activeTab?.focus();
  }

  /**
   * 1) Remove previously selected tab selected state
   * @fires tabChange
   */
  private handleTabSelect(e: CustomEvent<{ target: Event }>) {
    if (this.activeTab) {
      this.deactivatePreviouslySelectedTab(); /**1 */
    }
    this.activeTab = e.target as BlocksTab;
    const activeIndex = this.tabLists?.findIndex((tab) => tab === this.activeTab);

    this.activeIndex = activeIndex;
    this.activateTab();
    this.dispatch({
      eventName: 'bl-tab-change',
      detailObj: { value: this.activeTab, activeTabIndex: this.activeIndex }
    }) as BlocksTabChange;
  }

  /**
   * Updated lifecycle hook
   * 1) remove selected state from previously selected tab
   * 2) Checks to see if the old `activeIndex` property has been updated. If the new value
   * doesn't equal the old value, activate the proper tab
   */
  async updated(changedProperties: Map<string, unknown>) {
    changedProperties.forEach(async (oldValue, propName) => {
      if (propName === 'activeIndex' && this.activeIndex !== oldValue) {
        await this.updateComplete;
        if (this.activeTab) {
          this.deactivatePreviouslySelectedTab(); /**1 */
        }

        if (this.tabLists && this.activeIndex !== undefined && !this.tabLists[this.activeIndex].disabled) {
          this.activeTab = this.tabLists[this.activeIndex];
        }

        this.activateTab(); /* 2 */
        await this.updateComplete;

        if (this.activeTab && this.tabsHeaderEl) {
          scrollIntoView(this.activeTab, this.tabsHeaderEl, 0, 'horizontal');
        }
      }
    });
    if (changedProperties.has('variant') || changedProperties.has('behavior') || changedProperties.has('size')) {
      this.tabLists?.forEach((tabItem) => {
        this.setTabItemProps(tabItem);
      });
    }
    if (changedProperties.has('behavior') && this.behavior) {
      this.setIsStart();
      this.setIsEnd();
    }
  }

  private deactivatePreviouslySelectedTab(): void {
    if (this.activeTab) {
      this.activeTab.isActive = false;
    }

    const id = `#${this.activeTab?.ariaControls}`;
    const panel = this.querySelector<BlocksTabPanel>(`${id}`);
    panel?.removeAttribute('selected');
  }

  private handleKeydown(event: KeyboardEvent): void {
    const { target } = event;
    const focused = document.activeElement as BlocksTab;
    if (!focused.matches('cat-tab')) {
      return;
    }
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.setSelectedToNextTab(target as BlocksTab);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.setSelectedToPreviousTab(target as BlocksTab);
        break;
      case 'Home':
        event.preventDefault();
        if (this.tabLists) {
          this.setSelectedToNextTab(this.tabLists[this.tabLists.length - 1]);
        }
        break;
      case 'End':
        event.preventDefault();
        if (this.tabLists) {
          this.setSelectedToPreviousTab(this.tabLists[0]);
        }

        break;
    }
  }

  /**
   * 1) Get current selected Tab index then deactivate previously selected tab
   * 2) If current activeIndex is in first position then move the tab focus to last tab
   * @fires tabChange
   */
  private async setSelectedToPreviousTab(currentTab: BlocksTab) {
    const activeIndex = currentTab.index; /**1 */
    let tabListLength;
    if (this.tabLists) {
      tabListLength = this.tabLists.length - 1;
    }
    this.deactivatePreviouslySelectedTab(); /**1 */

    if (activeIndex === 0) {
      /**2 */
      let previousIndex = tabListLength;
      while (this.tabLists && previousIndex !== undefined && this.tabLists[previousIndex].shadowRoot?.querySelector('.cat-is-disabled')) {
        if (previousIndex === 0) {
          previousIndex = tabListLength;
        } else {
          previousIndex = previousIndex - 1;
        }
      }
      this.activeIndex = previousIndex;
      if (this.tabLists && previousIndex !== undefined) {
        this.activeTab = this.tabLists[previousIndex];
      }

      this.activateTab();
      await this.activeTab?.updateComplete;
    } else {
      let previousIndex;
      if (activeIndex !== undefined) {
        previousIndex = activeIndex - 1;
      }
      while (this.tabLists && previousIndex !== undefined && this.tabLists[previousIndex].shadowRoot?.querySelector('.cat-is-disabled')) {
        if (previousIndex === 0) {
          previousIndex = tabListLength;
        } else {
          previousIndex = previousIndex - 1;
        }
      }
      this.activeIndex = previousIndex;
      if (this.tabLists && previousIndex !== undefined) {
        this.activeTab = this.tabLists[previousIndex];
      }

      this.activateTab();
      await this.activeTab?.updateComplete;
    }

    this.focusTabElement();
    if (this.activeTab) {
      this.dispatch({
        eventName: 'bl-tab-change',
        detailObj: { value: this.activeTab, activeTabIndex: this.activeIndex }
      }) as BlocksTabChange;
    }
  }

  /**
   * 1) Get current selected Tab index then deactivate previously selected tab
   * 2) If current activeIndex is in last position then move the tab focus to first tab
   * @fires tabChange
   */
  private async setSelectedToNextTab(currentTab: BlocksTab) {
    const activeIndex = currentTab.index;
    this.deactivatePreviouslySelectedTab();
    let tabListLength;
    if (this.tabLists) {
      tabListLength = this.tabLists.length - 1;
    }
    if (activeIndex === tabListLength) {
      let nextIndex = 0;
      while (this.tabLists && nextIndex !== undefined && this.tabLists[nextIndex].shadowRoot?.querySelector('.cat-is-disabled')) {
        if (nextIndex === tabListLength) {
          nextIndex = 0;
        } else {
          nextIndex = nextIndex + 1;
        }
      }
      this.activeIndex = nextIndex;
      if (this.tabLists) {
        this.activeTab = this.tabLists[nextIndex];
      }

      this.activateTab();
      await this.activeTab?.updateComplete;
    } else {
      let nextIndex;
      if (activeIndex !== undefined) {
        nextIndex = activeIndex + 1;
      }

      while (this.tabLists && nextIndex !== undefined && this.tabLists[nextIndex].shadowRoot?.querySelector('.cat-is-disabled')) {
        if (nextIndex === tabListLength) {
          nextIndex = 0;
        } else {
          nextIndex = nextIndex + 1;
        }
      }
      this.activeIndex = nextIndex;
      if (this.tabLists && nextIndex !== undefined) {
        this.activeTab = this.tabLists[nextIndex];
      }

      this.activateTab();
      await this.activeTab?.updateComplete;
    }

    this.focusTabElement();

    if (this.activeTab) {
      this.dispatch({
        eventName: 'bl-tab-change',
        detailObj: { value: this.activeTab, activeTabIndex: this.activeIndex }
      }) as BlocksTabChange;
    }
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-tabs', {
      'cat-c-tabs--large': this.size === 'lg' && this.variant !== 'contrast',
      'cat-c-tabs--contrast': this.variant === 'contrast',
      'cat-c-tabs-span-container': this.variant === 'span-container',
      'cat-c-tabs--responsive-full-width': this.behavior === 'responsive-full-width',
      'cat-c-tabs--full-width': this.behavior === 'full-width',
      'cat-is-start': this.isStart === true,
      'cat-is-end': this.isEnd === true
    });

    return html`
      <div part="base" class="${componentClassName}">
        <header role="none" part="header" class="cat-c-tabs__header">
          <div part="list" class="cat-c-tabs__list" role="tablist" @scroll=${this.onScroll}>
            <slot
              part="part-default"
              @keydown=${this.handleKeydown}
              @bl-tab-select=${this.handleTabSelect}
              @slotchange=${this.syncTabAndSetActive}
            ></slot>
          </div>
        </header>
        <div part="body" class="cat-c-tabs__body">
          <slot part="part-panel" name="panel" @slotchange=${this.syncTabAndSetActive}></slot>
        </div>
      </div>
    `;
  }
}

if (customElements.get('cat-tabs') === undefined) {
  customElements.define('cat-tabs', BlocksTabs);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-tabs': BlocksTabs;
  }
}
